import type { ProjectRevisionSummary, ProjectRole } from '@bigint-project/cloud-contracts';
import { useEffect, useState, type ReactElement } from 'react';

import type { CloudApi, Etagged } from '../api/client';
import { errorMessage } from '../api/client';
import type { ProjectDocument } from '@bigint-project/cloud-contracts';
import { EmptyMessage, ErrorMessage, Loading } from './Status';

interface RevisionsPanelProps {
  readonly api: CloudApi;
  readonly projectId: string;
  readonly role: ProjectRole;
  readonly etag: string;
  readonly mutationBlocked: boolean;
  readonly onRestored: (result: Etagged<ProjectDocument>) => void;
}

export function RevisionsPanel({
  api,
  projectId,
  role,
  etag,
  mutationBlocked,
  onRestored,
}: RevisionsPanelProps): ReactElement {
  const [revisions, setRevisions] = useState<readonly ProjectRevisionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyRevision, setBusyRevision] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listRevisions(projectId).then(
      (result) => {
        if (!cancelled) setRevisions(result);
      },
      (reason: unknown) => {
        if (!cancelled) setError(errorMessage(reason));
      },
    ).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [api, projectId]);

  const restore = async (revision: number): Promise<void> => {
    if (role !== 'owner' || mutationBlocked) return;
    setBusyRevision(revision);
    setError(null);
    try {
      onRestored(await api.restoreRevision(projectId, revision, etag));
      setRevisions(await api.listRevisions(projectId));
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusyRevision(null);
    }
  };

  return (
    <section className="panel" aria-labelledby="revisions-heading">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Revision ledger</p>
          <h2 id="revisions-heading">版本歷程</h2>
        </div>
        {role !== 'owner' && <span className="muted">只有 Owner 能還原版本</span>}
        {role === 'owner' && mutationBlocked && <span className="muted">請先完成目前的雲端儲存</span>}
      </div>
      {error !== null && <ErrorMessage message={error} />}
      {loading && <Loading />}
      {!loading && revisions.length === 0 && <EmptyMessage>尚無版本紀錄。</EmptyMessage>}
      <ol className="revision-list">
        {revisions.map((revision) => (
          <li key={revision.revision}>
            <div>
              <strong>rev {revision.revision}</strong> · {revision.reason}
              <p className="muted">
                {revision.actorDisplayName} · {new Date(revision.createdAt).toLocaleString('zh-TW')}
              </p>
            </div>
            <button
              type="button"
              className="secondary"
              disabled={role !== 'owner' || mutationBlocked || busyRevision !== null}
              onClick={() => void restore(revision.revision)}
            >
              {busyRevision === revision.revision ? '還原中…' : '還原為新版本'}
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
